import { useMemo, type CSSProperties } from 'react';
import { V_GRADES, getGradeIndex } from '@flash/shared';
import type { VGrade, GradeAssociationType } from '@flash/shared';

interface GradeAssociationSelectorProps {
  associationType: GradeAssociationType;
  exactGrade?: VGrade | undefined;
  minGrade?: VGrade | undefined;
  maxGrade?: VGrade | undefined;
  onAssociationTypeChange: (type: GradeAssociationType) => void;
  onExactGradeChange: (grade: VGrade) => void;
  onMinGradeChange: (grade: VGrade) => void;
  onMaxGradeChange: (grade: VGrade) => void;
}

/**
 * Selector for configuring how a color associates with grades
 */
export function GradeAssociationSelector({
  associationType,
  exactGrade,
  minGrade,
  maxGrade,
  onAssociationTypeChange,
  onExactGradeChange,
  onMinGradeChange,
  onMaxGradeChange,
}: GradeAssociationSelectorProps) {
  // Filter max grades to only show grades >= minGrade
  const maxGradeOptions = useMemo(() => {
    if (!minGrade) return V_GRADES;
    const minIndex = getGradeIndex(minGrade);
    return V_GRADES.filter((_, i) => i >= minIndex);
  }, [minGrade]);

  // Filter min grades to only show grades <= maxGrade
  const minGradeOptions = useMemo(() => {
    if (!maxGrade) return V_GRADES;
    const maxIndex = getGradeIndex(maxGrade);
    return V_GRADES.filter((_, i) => i <= maxIndex);
  }, [maxGrade]);

  const handleMinGradeChange = (grade: VGrade) => {
    onMinGradeChange(grade);
    // Auto-adjust max if it's now less than min
    if (maxGrade && getGradeIndex(grade) > getGradeIndex(maxGrade)) {
      onMaxGradeChange(grade);
    }
  };

  const handleMaxGradeChange = (grade: VGrade) => {
    onMaxGradeChange(grade);
    // Auto-adjust min if it's now greater than max
    if (minGrade && getGradeIndex(grade) < getGradeIndex(minGrade)) {
      onMinGradeChange(grade);
    }
  };

  const containerStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const radioGroupStyle: CSSProperties = {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  };

  const radioLabelStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--color-text-primary)',
  };

  const selectStyle: CSSProperties = {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid var(--color-border)',
    backgroundColor: 'var(--color-bg-secondary)',
    color: 'var(--color-text-primary)',
    fontSize: '14px',
    minWidth: '100px',
  };

  const rangeContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const labelStyle: CSSProperties = {
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
  };

  return (
    <div style={containerStyle}>
      <div style={radioGroupStyle}>
        <label style={radioLabelStyle}>
          <input
            type="radio"
            checked={associationType === 'none'}
            onChange={() => onAssociationTypeChange('none')}
          />
          No grade association
        </label>
        <label style={radioLabelStyle}>
          <input
            type="radio"
            checked={associationType === 'exact'}
            onChange={() => onAssociationTypeChange('exact')}
          />
          Exact grade
        </label>
        <label style={radioLabelStyle}>
          <input
            type="radio"
            checked={associationType === 'range'}
            onChange={() => onAssociationTypeChange('range')}
          />
          Grade range
        </label>
      </div>

      {associationType === 'exact' && (
        <div>
          <label style={labelStyle}>Grade</label>
          <select
            value={exactGrade || ''}
            onChange={(e) => onExactGradeChange(e.target.value as VGrade)}
            style={selectStyle}
          >
            <option value="">Select grade</option>
            {V_GRADES.map((grade) => (
              <option key={grade} value={grade}>
                {grade}
              </option>
            ))}
          </select>
        </div>
      )}

      {associationType === 'range' && (
        <div style={rangeContainerStyle}>
          <div>
            <label style={labelStyle}>Min grade</label>
            <select
              value={minGrade || ''}
              onChange={(e) => handleMinGradeChange(e.target.value as VGrade)}
              style={selectStyle}
            >
              <option value="">Select min</option>
              {minGradeOptions.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>
          <span style={{ marginTop: '20px', color: 'var(--color-text-secondary)' }}>to</span>
          <div>
            <label style={labelStyle}>Max grade</label>
            <select
              value={maxGrade || ''}
              onChange={(e) => handleMaxGradeChange(e.target.value as VGrade)}
              style={selectStyle}
            >
              <option value="">Select max</option>
              {maxGradeOptions.map((grade) => (
                <option key={grade} value={grade}>
                  {grade}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
